import { useState, useRef, useEffect } from 'react';
import { Send, Loader2, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ChatInputProps {
  onSend: (message: string) => void;
  isLoading: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export function ChatInput({ onSend, isLoading, placeholder, disabled }: ChatInputProps) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [value]);

  const handleSend = () => {
    const trimmed = value.trim();
    if (!trimmed || isLoading) return;
    onSend(trimmed);
    setValue('');
    // Reset height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t border-slate-200/80 bg-white/80 backdrop-blur-md p-3">
      <div
        className={cn(
          'flex items-end gap-2 rounded-2xl border border-slate-200/80 bg-white px-3 py-2 shadow-sm transition-all duration-200',
          'focus-within:border-workfinity-300 focus-within:shadow-md focus-within:ring-1 focus-within:ring-workfinity-100',
        )}
      >
        <Sparkles className="h-4 w-4 text-workfinity-400 flex-shrink-0 mb-1.5" />

        <textarea
          ref={textareaRef}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading || disabled}
          rows={1}
          placeholder={placeholder || (disabled ? 'Please complete the current step above...' : 'Describe your workflow or ask a question...')}
          className={cn(
            'flex-1 resize-none bg-transparent text-sm text-slate-800 placeholder:text-slate-400 outline-none',
            'min-h-[24px] max-h-[140px] leading-relaxed',
            (isLoading || disabled) && 'opacity-60 cursor-not-allowed',
          )}
        />

        <button
          onClick={handleSend}
          disabled={!value.trim() || isLoading || disabled}
          className={cn(
            'flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-200 mb-0.5',
            value.trim() && !isLoading
              ? 'bg-gradient-to-br from-workfinity-500 to-workfinity-600 text-white shadow-sm hover:shadow-md hover:scale-105'
              : 'bg-slate-100 text-slate-400 cursor-not-allowed',
          )}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
        </button>
      </div>

      <p className="text-[10px] text-slate-400 text-center mt-1.5">
        Press Enter to send · Shift + Enter for new line
      </p>
    </div>
  );
}
